import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { trpc } from "@/lib/trpc";
import { Plus, Phone, User, Star, RotateCcw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

// ---- Types ------------------------------------------------------------------

type ContactScopeV2 = "customer" | "contract" | "site" | "installation";
type CanonicalContactRole = "billing" | "technical" | "sales" | "legal" | "other";

interface ContactAssignmentV2 {
  assignmentId: number;
  contactId: number;
  scope: ContactScopeV2;
  businessPartnerId: number;
  customerContractId: number | null;
  siteId: number | null;
  installationId: number | null;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  jobTitle: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  role: string;
  isPrimary: boolean;
  visibleToTechnicians: boolean;
  validFrom: string | null;
  validUntil: string | null;
  notes: string | null;
}

// ---- Helpers ----------------------------------------------------------------

const scopeLabel: Record<ContactScopeV2, string> = {
  customer: "Cliente",
  contract: "Contrato",
  site: "Sede",
  installation: "Instalación",
};

const roleLabel: Record<string, string> = {
  billing: "Facturación",
  technical: "Técnico",
  sales: "Comercial",
  legal: "Legal",
  other: "Otro",
  primary: "Principal (legado)",
};

const scopeVariant: Record<ContactScopeV2, "default" | "secondary" | "outline"> = {
  customer: "default",
  contract: "secondary",
  site: "secondary",
  installation: "outline",
};

// ---- Empty form state -------------------------------------------------------

interface FormState {
  displayName: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  primaryEmail: string;
  primaryPhone: string;
  role: CanonicalContactRole;
  scope: ContactScopeV2;
  isPrimary: boolean;
  visibleToTechnicians: boolean;
  legacyPrimary: boolean;
}

const emptyForm = (): FormState => ({
  displayName: "",
  firstName: "",
  lastName: "",
  jobTitle: "",
  primaryEmail: "",
  primaryPhone: "",
  role: "technical",
  scope: "installation",
  isPrimary: false,
  visibleToTechnicians: false,
  legacyPrimary: false,
});

// ---- Component --------------------------------------------------------------

export function ContactsTab({
  installationId,
  businessPartnerId,
}: {
  installationId: number;
  businessPartnerId?: number | null;
}) {
  const utils = trpc.useUtils();

  const [showInactive, setShowInactive] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [editingAssignmentId, setEditingAssignmentId] = useState<number | null>(null);
  const [deletingAssignment, setDeletingAssignment] = useState<ContactAssignmentV2 | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());

  const contactsQuery = trpc.canonicalContacts.resolveForInstallation.useQuery({
    installationId,
    includeInactive: showInactive,
  });

  const contacts: ContactAssignmentV2[] = contactsQuery.data ?? [];
  const resolvedBusinessPartnerId = businessPartnerId ?? contacts[0]?.businessPartnerId ?? null;

  const invalidate = () => utils.canonicalContacts.resolveForInstallation.invalidate({ installationId });

  const createMutation = trpc.canonicalContacts.createForInstallation.useMutation({
    onSuccess: () => { invalidate(); setIsOpen(false); setForm(emptyForm()); toast.success("Contacto añadido"); },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = trpc.canonicalContacts.update.useMutation({
    onSuccess: () => { invalidate(); setIsOpen(false); setEditingAssignmentId(null); setForm(emptyForm()); toast.success("Contacto actualizado"); },
    onError: (e) => toast.error(e.message),
  });

  const deactivateMutation = trpc.canonicalContacts.deactivate.useMutation({
    onSuccess: () => { invalidate(); toast.success("Contacto desactivado"); },
    onError: (e) => toast.error(e.message),
  });

  const reactivateMutation = trpc.canonicalContacts.reactivate.useMutation({
    onSuccess: () => { invalidate(); toast.success("Contacto reactivado"); },
    onError: (e) => toast.error(e.message),
  });

  const setPrimaryMutation = trpc.canonicalContacts.setPrimary.useMutation({
    onSuccess: () => { invalidate(); toast.success("Contacto principal actualizado"); },
    onError: (e) => toast.error(e.message),
  });

  const openCreate = () => {
    setEditingAssignmentId(null);
    setForm(emptyForm());
    setIsOpen(true);
  };

  const openEdit = (c: ContactAssignmentV2) => {
    setEditingAssignmentId(c.assignmentId);
    setForm({
      displayName: c.displayName,
      firstName: c.firstName ?? "",
      lastName: c.lastName ?? "",
      jobTitle: c.jobTitle ?? "",
      primaryEmail: c.primaryEmail ?? "",
      primaryPhone: c.primaryPhone ?? "",
      // Legacy primary is display-only in this canonical form; it is never
      // presented as or rewritten to a canonical category.
      role: (c.role === "primary" ? "other" : c.role) as CanonicalContactRole,
      scope: c.scope,
      isPrimary: c.isPrimary,
      visibleToTechnicians: c.visibleToTechnicians,
      legacyPrimary: c.role === "primary",
    });
    setIsOpen(true);
  };

  const handleSubmit = () => {
    if (!form.displayName.trim()) return toast.error("El nombre es obligatorio");

    if (editingAssignmentId != null) {
      if (!resolvedBusinessPartnerId) return toast.error("businessPartnerId no disponible");
      updateMutation.mutate({
        assignmentId: editingAssignmentId,
        businessPartnerId: resolvedBusinessPartnerId,
        displayName: form.displayName.trim(),
        firstName: form.firstName.trim() || null,
        lastName: form.lastName.trim() || null,
        jobTitle: form.jobTitle.trim() || null,
        primaryEmail: form.primaryEmail.trim() || null,
        primaryPhone: form.primaryPhone.trim() || null,
        visibleToTechnicians: form.visibleToTechnicians,
      });
    } else {
      createMutation.mutate({
        installationId,
        displayName: form.displayName.trim(),
        firstName: form.firstName.trim() || null,
        lastName: form.lastName.trim() || null,
        jobTitle: form.jobTitle.trim() || null,
        primaryEmail: form.primaryEmail.trim() || null,
        primaryPhone: form.primaryPhone.trim() || null,
        role: form.role,
        scope: form.scope,
        isPrimary: form.isPrimary,
        visibleToTechnicians: form.visibleToTechnicians,
      });
    }
  };

  const isBusy = createMutation.isPending || updateMutation.isPending;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Contactos</CardTitle>
          <CardDescription>Contactos canónicos de esta instalación</CardDescription>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} />
            Mostrar inactivos
          </label>
          <Dialog open={isOpen} onOpenChange={(open) => {
            setIsOpen(open);
            if (!open) { setEditingAssignmentId(null); setForm(emptyForm()); }
          }}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={openCreate}>
                <Plus className="w-4 h-4 mr-2" /> Agregar Contacto
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingAssignmentId != null ? "Editar Contacto" : "Nuevo Contacto"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} placeholder="Nombre completo" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} placeholder="Nombre" />
                  </div>
                  <div className="space-y-2">
                    <Label>Apellidos</Label>
                    <Input value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} placeholder="Apellidos" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Cargo</Label>
                  <Input value={form.jobTitle} onChange={e => setForm(f => ({ ...f, jobTitle: e.target.value }))} placeholder="Ej. Jefe de Mantenimiento" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={form.primaryEmail} onChange={e => setForm(f => ({ ...f, primaryEmail: e.target.value }))} placeholder="email@ejemplo.es" type="email" />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input value={form.primaryPhone} onChange={e => setForm(f => ({ ...f, primaryPhone: e.target.value }))} placeholder="600 000 000" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Categoría</Label>
                      {form.legacyPrimary ? (
                        <div className="rounded-md border px-3 py-2 text-sm text-muted-foreground">Principal (legado)</div>
                      ) : (
                        <Select value={form.role} onValueChange={(v) => setForm(f => ({ ...f, role: v as CanonicalContactRole, legacyPrimary: false }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="technical">Técnico</SelectItem>
                            <SelectItem value="billing">Facturación</SelectItem>
                            <SelectItem value="sales">Comercial</SelectItem>
                            <SelectItem value="legal">Legal</SelectItem>
                            <SelectItem value="other">Otro</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                  </div>
                  {editingAssignmentId == null && (
                    <div className="space-y-2">
                      <Label>Ámbito</Label>
                      <Select value={form.scope} onValueChange={(v) => setForm(f => ({ ...f, scope: v as ContactScopeV2 }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="installation">Instalación</SelectItem>
                          <SelectItem value="contract">Contrato</SelectItem>
                          <SelectItem value="site">Sede</SelectItem>
                          <SelectItem value="customer">Cliente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-6">
                  {editingAssignmentId == null && (
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Switch checked={form.isPrimary} onCheckedChange={v => setForm(f => ({ ...f, isPrimary: v }))} />
                      Principal
                    </label>
                  )}
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Switch checked={form.visibleToTechnicians} onCheckedChange={v => setForm(f => ({ ...f, visibleToTechnicians: v }))} />
                    Visible a técnicos
                  </label>
                </div>
                <Button onClick={handleSubmit} disabled={isBusy} className="w-full">
                  {editingAssignmentId != null ? "Actualizar" : "Guardar"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={deletingAssignment != null} onOpenChange={(open) => !open && setDeletingAssignment(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>¿Eliminar este contacto?</DialogTitle>
                <DialogDescription className="space-y-1 pt-2">
                  <span>Dejará de mostrarse en este ámbito y en sus herencias.</span>
                  <br />
                  <span>El historial se conservará.</span>
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDeletingAssignment(null)}>
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  disabled={deactivateMutation.isPending}
                  onClick={() => {
                    if (deletingAssignment?.businessPartnerId) {
                      deactivateMutation.mutate(
                        { assignmentId: deletingAssignment.assignmentId, businessPartnerId: deletingAssignment.businessPartnerId },
                        {
                          onSuccess: () => setDeletingAssignment(null),
                        },
                      );
                    }
                  }}
                >
                  Eliminar
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Contacto</TableHead>
              <TableHead>Email / Teléfono</TableHead>
              <TableHead>Ámbito</TableHead>
              <TableHead>Categoría</TableHead>
              <TableHead>Visible técnicos</TableHead>
              <TableHead>Principal</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contactsQuery.isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center">Cargando...</TableCell></TableRow>
            ) : contacts.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">No hay contactos.</TableCell></TableRow>
            ) : contacts.map((c) => (
              <TableRow key={c.assignmentId} className={c.validUntil != null ? "opacity-50" : ""}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div>
                      <div className="font-medium flex items-center gap-1">
                        {c.displayName}
                      </div>
                      {c.jobTitle && <div className="text-xs text-muted-foreground">{c.jobTitle}</div>}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm space-y-0.5">
                    {c.primaryPhone && (
                      <div className="flex items-center gap-1">
                        <Phone className="w-3 h-3 text-muted-foreground" />
                        {c.primaryPhone}
                      </div>
                    )}
                    {c.primaryEmail && <div className="text-muted-foreground truncate max-w-[180px]">{c.primaryEmail}</div>}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={scopeVariant[c.scope]}>{scopeLabel[c.scope]}</Badge>
                </TableCell>
                <TableCell className="text-sm">{roleLabel[c.role] ?? c.role}</TableCell>
                <TableCell>
                  <Switch
                    checked={c.visibleToTechnicians}
                    disabled={c.validUntil != null || updateMutation.isPending}
                    onCheckedChange={(checked) => {
                      if (!c.businessPartnerId) return toast.error("businessPartnerId no disponible");
                      updateMutation.mutate({
                        assignmentId: c.assignmentId,
                        businessPartnerId: c.businessPartnerId,
                        visibleToTechnicians: checked,
                      });
                    }}
                  />
                </TableCell>
                <TableCell>
                  {c.validUntil == null && c.isPrimary ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : c.validUntil == null && c.businessPartnerId ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={setPrimaryMutation.isPending}
                      onClick={() => setPrimaryMutation.mutate({ assignmentId: c.assignmentId, businessPartnerId: c.businessPartnerId })}
                    >
                      Hacer principal
                    </Button>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {c.validUntil == null ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEdit(c)}
                        >
                          Editar
                        </Button>
                        {c.businessPartnerId && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive"
                            onClick={() => setDeletingAssignment(c)}
                          >
                            Eliminar
                          </Button>
                        )}
                      </>
                    ) : (
                      c.businessPartnerId && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => reactivateMutation.mutate({ assignmentId: c.assignmentId, businessPartnerId: c.businessPartnerId })}
                          disabled={reactivateMutation.isPending}
                        >
                          Reactivar
                        </Button>
                      )
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
